import React, { useState, useEffect, useCallback, useMemo } from "react";
import useAuth from "./auth/useAuth";

const ProfilePage = () => {
  const { isAuthenticated, redirectToAuth, user, loading } = useAuth();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [preferences, setPreferences] = useState({ genders: [], ageCategories: [] });
  const [profile, setProfile] = useState({});
  const [fetchStatus, setFetchStatus] = useState({ loading: false, error: null });
  const token = useMemo(() => localStorage.getItem("authToken"), []);

  const [workouts, setWorkouts] = useState([]);
  const [workoutStatus, setWorkoutStatus] = useState({ loading: false, error: null });


  useEffect(() => {
    if (!loading && !isAuthenticated) {
      console.warn("User not authenticated. Redirecting...");
      redirectToAuth();
    }
  }, [isAuthenticated, loading, redirectToAuth]);
  
  useEffect(() => {
    if (user && Object.keys(user).length > 0) {
      console.log("User data available:", user);
      setProfile(user);
    } else {
      console.warn("User data not available or empty");
    }
  }, [user]);
     

  const fetchPreferencesData = useCallback(async () => {
    setFetchStatus({ loading: true, error: null });
    console.log("Starting preferences fetch with token:", token?.substring(0, 10) + "...");
  
    try {
      const apiUrl = '/api/v1/profile-preferences';
      console.log("Fetching from:", apiUrl);
  
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });
  
      if (!response.ok) {
        const errorText = await response.text();
        console.error("API error response:", errorText);
        throw new Error(`HTTP error! Status: ${response.status}, Details: ${errorText}`);
      }
  
      const data = await response.json();
  
      if (!data.genders || !data.ageCategories) {
        console.warn("Preferences data missing expected properties:", data);
      }
  
      setPreferences(data);
      setFetchStatus({ loading: false, error: null });
  
    } catch (error) {
      console.error('Error fetching preferences data:', error);
      setFetchStatus({ loading: false, error: error.message });
  
      // fallback attempt
      console.log("Attempting alternative fetch method...");
      try {
        const fallbackResponse = await fetch('/api/v1/profile-preferences', {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          credentials: 'include',
        });
  
        console.log("Fallback response status:", fallbackResponse.status);
  
        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          console.log("Fallback data received:", fallbackData);
          setPreferences(fallbackData);
          setFetchStatus({ loading: false, error: null });
        }
      } catch (fallbackError) {
        console.error("Fallback method also failed:", fallbackError);
      }
    }
  }, [token]);
  
  useEffect(() => {
    if (user && token) {
      console.log("Token available:", !!token);
      fetchPreferencesData();
    } else if (user) {
      console.warn("No token available for API call");
    }
  }, [user, token, fetchPreferencesData]);


  useEffect(() => {
    console.log("Current preferences state:", preferences);
  }, [preferences]);

  const handleEditModal = () => {
    setIsEditModalOpen(!isEditModalOpen);
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async () => {
    try {
      const response = await fetch("/api/v1/update-profile", {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(profile),
      });
      
      console.log("Profile update response:", response.status);
      
      if (response.ok) {
        alert("Profile updated successfully");
        setIsEditModalOpen(false);
      } else {
        const errorText = await response.text();
        console.error("Profile update error:", errorText);
        alert(`Failed to update profile: ${response.status} ${errorText}`);
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      alert(`Error updating profile: ${error.message}`);
    }
  };

  useEffect(() => {
    const fetchWorkouts = async () => {
      setWorkoutStatus({ loading: true, error: null });
  
      try {
        const response = await fetch("/api/v1/get-workout", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
  
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to fetch workouts");
        }
  
        const data = await response.json();
        setWorkouts(data.workouts);
      } catch (err) {
        console.error("Error fetching workouts:", err);
        setWorkoutStatus({ loading: false, error: err.message });
      } finally {
        setWorkoutStatus((prev) => ({ ...prev, loading: false }));
      }
    };
  
    if (token) {
      fetchWorkouts();
    }
  }, [token]);
  

  return (
    <div className="section" id="user-profile">

      <div className="header">
      <img 
          src={profile.picture} 
          alt={`${profile.displayName || profile.email || "Guest"}'s profile`} 
          className="profile-picture" 
        />
        <h1>{profile.displayName || profile.email || "Guest"}</h1>
        <h3>{profile.displayText || "Welcome to your profile!"}</h3>
        <button className="card-container-button" onClick={handleEditModal}>
          Edit Profile
        </button>
        <hr className="custom-line" />
      </div>

      <div className="section" id="profile-nutrition">
        <h2>Nutrition</h2>
        <div className="list-container">
          <div className="card">
            <h3>Supplements</h3>
          </div>
          <div className="card">
            <h3>Regular Food</h3>
          </div>
        </div>
      </div>

      <div className="section" id="profile-performance">
        <h2>Performance</h2>
        <div className="list-container">
          <div className="card">
            <h3>Equipment</h3>
          </div>
          <div className="card">
            <h3>Diagnostics</h3>
          </div>
        </div>
      </div>

<div className="section" id="profile-workout">
  <h2>Workout Section</h2>

  {workoutStatus.loading && <p>Loading workouts...</p>}
  {workoutStatus.error && <p style={{ color: "red" }}>Missing: {workoutStatus.error}</p>}

  {!workoutStatus.loading && !workoutStatus.error && workouts.length === 0 && (
    <p>No saved workouts. Use the Workout Gen to create and save a workout.</p>
  )}

  {!workoutStatus.loading && !workoutStatus.error && workouts.length > 0 && (
    <div>
      {workouts.map((workout, index) => {
        const plan = workout.workout_data;

        return (
          <div key={workout.workout_id || index} style={{ marginBottom: "2rem", padding: "1rem", border: "1px solid #ccc", borderRadius: "8px" }}>
            <h2>Workout {index + 1}: {workout.name || "Unnamed Plan"}</h2>
            <p><strong>Description:</strong> {workout.description}</p>
            <p><strong>Generated:</strong> {new Date(workout.generated_at).toLocaleString()}</p>

            {plan?.SessionStructure?.length > 0 ? (
              <div>
                {/* <h3>Weekly Schedule</h3> */}
                {plan.SessionStructure.map((session, sIndex) => (
                  <div key={sIndex} style={{ marginTop: "1rem" }}>
                    <h3>{session.Session}: {session.Focus || "No focus provided"}</h3>
                    {session.FrequencyAndSets && <p>{session.FrequencyAndSets}</p>}

                    <ul style={{ paddingLeft: "1.5rem" }}>
                      {session.Exercises?.length > 0 ? (
                        session.Exercises.map((exercise, eIndex) => (
                          <li key={eIndex}>
                            <strong>{exercise.Name}</strong>
                            {exercise.Target && <> – Target: {exercise.Target}</>}
                            {exercise.Reps && <> – Reps: {exercise.Reps}</>}
                            {exercise.Duration && <> – Duration: {exercise.Duration}</>}
                            {exercise.Progression && <> – Progression: {exercise.Progression}</>}
                          </li>
                        ))
                      ) : (
                        <li>Limit exercises and focus on recovery</li>
                      )}
                      {session.Options?.length > 0 && (
                        <li>Options: {session.Options.join(", ")}</li>
                      )}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              <p>No workout sessions available.</p>
            )}
          </div>
        );
      })}
    </div>
  )}
</div>


      <div className="section" id="profile-workout">
        <h2>Biomarkers Section</h2>
        <p>Track and trend your health</p>
      </div>

      {isEditModalOpen && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div className="list-section">
              <div className="list-container">
                <h3>Edit Profile</h3>
                <div className="basic-input">
                  <form>
                  <label>
                      Display Name:
                      <input
                        type="displayName"
                        name="displayName"
                        value={profile.displayName}
                        onChange={handleProfileChange}
                      />
                    </label>
                    <br />
                    <label>
                      Display Text:
                      <input
                        type="displayText"
                        name="displayText"
                        value={profile.displayText}
                        onChange={handleProfileChange}
                      />
                    </label>
                    <br />
                    <label>
                      Email Address:
                      <input
                        type="email"
                        name="email"
                        value={profile.email || ""}
                        onChange={handleProfileChange}
                      />
                    </label>
                    <br />
                    <label>
                      Password:
                      <input
                        type="password"
                        name="password"
                        value={profile.password || "*****"}
                        onChange={handleProfileChange}
                      />
                    </label>
                    <br />
                    <label>
                      Gender:
                      <select
                        name="gender"
                        value={profile.gender || ""}
                        onChange={handleProfileChange}
                      >
                        {preferences.genders.map((gender) => (
                          <option key={gender} value={gender}>
                            {gender}
                          </option>
                        ))}
                      </select>
                    </label>
                    <br />
                    <label>
                      Age Category:
                      <select
                        name="ageCategory"
                        value={profile.ageCategory || ""}
                        onChange={handleProfileChange}
                      >
                        {preferences.ageCategories.map((category) => (
                          <option key={category} value={category}>
                            {category}
                          </option>
                        ))}
                      </select>
                    </label>
                    <br />
                    <button type="button" onClick={handleSaveProfile}>
                      Save Profile
                    </button>
                    <button type="button" onClick={handleEditModal}>
                      Close Profile
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  modal: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    width: "320px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },

};

export default ProfilePage;

